import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./i18n"; // bootstraps i18next before any component renders
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth, RequirePermission, RequireAnyRole } from "./auth/RequireAuth";
import { AppShell } from "./components/AppShell";
import { RouteErrorBoundary } from "./components/ErrorBoundary";
import { ToastProvider } from "./components/Toast";
import { ThemeProvider } from "./theme/ThemeProvider";
import { ConfirmProvider } from "./components/ConfirmDialog";
import DashboardPage from "./pages/DashboardPage";
import ChatPage from "./pages/chat/ChatPage";
import AdminCompanyDetailPage from "./pages/admin/AdminCompanyDetailPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForbiddenPage from "./pages/ForbiddenPage";
import ProfilePage from "./pages/ProfilePage";
import ContactPage from "./pages/ContactPage";
import AdminCompaniesPage from "./pages/admin/AdminCompaniesPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminPermissionsPage from "./pages/admin/AdminPermissionsPage";
import AdminInvitationsPage from "./pages/admin/AdminInvitationsPage";
import AdminCategoriesPage from "./pages/admin/catalog/AdminCategoriesPage";
import AdminProductsPage from "./pages/admin/catalog/AdminProductsPage";
import AdminProductEditorPage from "./pages/admin/catalog/AdminProductEditorPage";
import CatalogIndexPage from "./pages/catalog/CatalogIndexPage";
import CatalogCategoryPage from "./pages/catalog/CatalogCategoryPage";
import CatalogProductPage from "./pages/catalog/CatalogProductPage";
import MaterialsPage from "./pages/materials/MaterialsPage";
import AdminMaterialsPage from "./pages/admin/materials/AdminMaterialsPage";
import NewsListPage from "./pages/news/NewsListPage";
import NewsDetailPage from "./pages/news/NewsDetailPage";
import AdminNewsPage from "./pages/admin/news/AdminNewsPage";
import AdminNewsEditorPage from "./pages/admin/news/AdminNewsEditorPage";
import ContractsPage from "./pages/ContractsPage";
import AdminContractsPage from "./pages/admin/AdminContractsPage";
import TraceabilityPage from "./pages/admin/TraceabilityPage";
import WarehousePage from "./pages/warehouse/WarehousePage";
import ContractFormPage from "./pages/admin/ContractFormPage";
import ProcurementContractsPage from "./pages/procurement/ProcurementContractsPage";
import ProcurementContractDetailPage from "./pages/procurement/ProcurementContractDetailPage";
import ProcurementImportsPage from "./pages/procurement/ProcurementImportsPage";
import LotsPage from "./pages/procurement/LotsPage";
import SupplierSettlementsPage from "./pages/procurement/SupplierSettlementsPage";
import CombinedLedgerPage from "./pages/procurement/CombinedLedgerPage";
import LotDetailPage from "./pages/procurement/LotDetailPage";
import IntakeLotDetailPage from "./pages/procurement/IntakeLotDetailPage";
import AdminQualityParametersPage from "./pages/admin/quality/AdminQualityParametersPage";
import AdminTestPlansPage from "./pages/admin/quality/AdminTestPlansPage";
import QualityDispatchReleasePage from "./pages/admin/quality/QualityDispatchReleasePage";
import LabArchivePage from "./pages/admin/quality/LabArchivePage";
import QualityControlPointsPage from "./pages/admin/quality/QualityControlPointsPage";
import QualityLotReadingsPage from "./pages/admin/quality/QualityLotReadingsPage";
import QualityWorkQueuePage from "./pages/admin/quality/QualityWorkQueuePage";
import QualityPendingVerificationPage from "./pages/admin/quality/QualityPendingVerificationPage";
import ProductionStepsPage from "./pages/admin/quality/ProductionStepsPage";
import ExternalAnalysesPage from "./pages/admin/quality/ExternalAnalysesPage";
import ExternalLaboratoriesPage from "./pages/admin/quality/ExternalLaboratoriesPage";
import QualityClassesPage from "./pages/admin/quality/QualityClassesPage";
import RetainedSamplesPage from "./pages/admin/quality/RetainedSamplesPage";
import LotDocumentsPage from "./pages/admin/quality/LotDocumentsPage";
import DispatchOrdersPage from "./pages/sales/DispatchOrdersPage";
import AdminSettlementRulesPage from "./pages/admin/settlement/AdminSettlementRulesPage";
import AdminProcurementContractsPage from "./pages/procurement/AdminProcurementContractsPage";
import DeliveriesPage from "./pages/procurement/DeliveriesPage";
import DeliveryDetailPage from "./pages/procurement/DeliveryDetailPage";
import SupplierFinancePage from "./pages/procurement/SupplierFinancePage";
import SupplierFinanceCompanyPage from "./pages/procurement/SupplierFinanceCompanyPage";
import OrdersPage from "./pages/OrdersPage";
import OrderEditorPage from "./pages/OrderEditorPage";
import OffersPage from "./pages/OffersPage";
import OffersBoardPage from "./pages/OffersBoardPage";
import OffersFunnelPage from "./pages/OffersFunnelPage";
import OfferEditorPage from "./pages/OfferEditorPage";
import LeadsPage from "./pages/LeadsPage";
import LeadsBoardPage from "./pages/LeadsBoardPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import ComplaintNewPage from "./pages/ComplaintNewPage";
import ComplaintDetailPage from "./pages/ComplaintDetailPage";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import ForecastsPage from "./pages/ForecastsPage";
import FinancePage from "./pages/finance/FinancePage";
import FinanceCompanyPage from "./pages/finance/FinanceCompanyPage";
import AdminErpOutboxPage from "./pages/admin/AdminErpOutboxPage";
import AdminExchangeRatesPage from "./pages/admin/AdminExchangeRatesPage";
import AdminAuditLogPage from "./pages/admin/AdminAuditLogPage";
import AdminEmailPreviewPage from "./pages/admin/AdminEmailPreviewPage";
import AdminJwtKeysPage from "./pages/admin/AdminJwtKeysPage";
import AdminMarketsPage from "./pages/admin/AdminMarketsPage";
import AdminOfferStagesPage from "./pages/admin/AdminOfferStagesPage";
import AdminKpiPage from "./pages/admin/AdminKpiPage";
import MarketsOverviewPage from "./pages/MarketsOverviewPage";
import ManagementReportPage from "./pages/reports/ManagementReportPage";
import OrderImportReviewPage from "./pages/orders/OrderImportReviewPage";
import TmOverviewPage from "./pages/tm/TmOverviewPage";
import TmTasksPage from "./pages/tm/TmTasksPage";
import TmTaskNewPage from "./pages/tm/TmTaskNewPage";
import TmTaskDetailPage from "./pages/tm/TmTaskDetailPage";
import TmProjectsPage from "./pages/tm/TmProjectsPage";
import TmProjectNewPage from "./pages/tm/TmProjectNewPage";
import TmProjectDetailPage from "./pages/tm/TmProjectDetailPage";
import { ScrollToTop } from "./components/ScrollToTop";
import TmMeetingsPage from "./pages/tm/TmMeetingsPage";
import TmMeetingNewPage from "./pages/tm/TmMeetingNewPage";
import TmMeetingDetailPage from "./pages/tm/TmMeetingDetailPage";
import TmTeamsPage from "./pages/tm/TmTeamsPage";
import TmActivityPage from "./pages/tm/TmActivityPage";
import MailSettingsPage from "./pages/MailSettingsPage";
import TripsPage from "./pages/transport/TripsPage";
import TripDetailPage from "./pages/transport/TripDetailPage";
import GatePage from "./pages/transport/GatePage";
import CarriersPage from "./pages/admin/transport/CarriersPage";
import "./styles.css";

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <ToastProvider>
        <ThemeProvider>
        <ConfirmProvider>
        <Routes>
          {/* Public auth routes (no shell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />

          {/* Authenticated routes inside the new AppShell */}
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            {/* A screen that throws must not take the menu with it, so the boundary goes INSIDE the
                shell and around the routed content only. Mounted as a layout route, it also sees the
                path, which is what clears the panel when the reader clicks another menu entry. */}
            <Route element={<RouteErrorBoundary />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            {/* One route with the channel optional, so moving from the list to a channel keeps the screen. */}
            <Route
              path="/chat/:channelId?"
              element={
                <RequirePermission permission="section:chat:read">
                  <ChatPage />
                </RequirePermission>
              }
            />
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/contact"
              element={
                <RequireAnyRole roles={["CUST_MANAGER", "CUST_PROCUREMENT"]}>
                  <ContactPage />
                </RequireAnyRole>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <RequirePermission permission="section:companies:read">
                  <AdminCompaniesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/markets"
              element={
                <RequirePermission permission="section:market_settings:read">
                  <AdminMarketsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/offer-stages"
              element={
                <RequirePermission permission="section:offer_stages:read">
                  <AdminOfferStagesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/kpi"
              element={
                <RequirePermission permission="section:kpi:read">
                  <AdminKpiPage />
                </RequirePermission>
              }
            />
            <Route
              path="/markets"
              element={
                <RequirePermission permission="section:market_overview:read">
                  <MarketsOverviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/reports/sales"
              element={
                <RequirePermission permission="section:management_report:read">
                  <ManagementReportPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/companies/:id"
              element={
                <RequirePermission permission="section:companies:read">
                  <AdminCompanyDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequirePermission permission="section:users:read">
                  <AdminUsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/invitations"
              element={
                <RequirePermission permission="section:invitations:read">
                  <AdminInvitationsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/categories"
              element={
                <RequirePermission permission="section:catalog:read">
                  <AdminCategoriesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/products"
              element={
                <RequirePermission permission="section:catalog:read">
                  <AdminProductsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/products/:id"
              element={
                <RequirePermission permission="section:catalog:write">
                  <AdminProductEditorPage />
                </RequirePermission>
              }
            />

            {/* Catalog browse */}
            <Route
              path="/catalog"
              element={
                <RequirePermission permission="section:catalog:read">
                  <CatalogIndexPage />
                </RequirePermission>
              }
            />
            <Route
              path="/catalog/:slug"
              element={
                <RequirePermission permission="section:catalog:read">
                  <CatalogCategoryPage />
                </RequirePermission>
              }
            />
            <Route
              path="/catalog/:categorySlug/:sku"
              element={
                <RequirePermission permission="section:catalog:read">
                  <CatalogProductPage />
                </RequirePermission>
              }
            />

            {/* Materials */}
            <Route path="/materials" element={<RequirePermission permission="section:materials:read"><MaterialsPage /></RequirePermission>} />
            <Route
              path="/admin/materials"
              element={
                <RequirePermission permission="section:materials:read">
                  <AdminMaterialsPage />
                </RequirePermission>
              }
            />

            {/* News */}
            <Route path="/news" element={<RequirePermission permission="section:news:read"><NewsListPage /></RequirePermission>} />
            <Route path="/news/:slug" element={<RequirePermission permission="section:news:read"><NewsDetailPage /></RequirePermission>} />
            <Route
              path="/admin/news"
              element={
                <RequirePermission permission="section:news:read">
                  <AdminNewsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/news/:id"
              element={
                <RequirePermission permission="section:news:write">
                  <AdminNewsEditorPage />
                </RequirePermission>
              }
            />

            {/* Contracts */}
            <Route
              path="/contracts"
              element={
                <RequirePermission permission="section:sales_contracts:read">
                  <ContractsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/contracts"
              element={
                <RequirePermission permission="section:sales_contracts:read">
                  <AdminContractsPage />
                </RequirePermission>
              }
            />

            {/* Unos ugovora je stranica, ne iskačući prozor: uslovi i stavke ne staju u njega, a
                ugovor se i ne unosi u jednom dahu. */}
            <Route
              path="/admin/contracts/new"
              element={
                <RequirePermission permission="section:sales_contracts:write">
                  <ContractFormPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/contracts/:id/edit"
              element={
                <RequirePermission permission="section:sales_contracts:write">
                  <ContractFormPage />
                </RequirePermission>
              }
            />

            {/* Sledljivost LOT-a: korak nazad u proizvodnju i korak napred do kupaca */}
            <Route
              path="/traceability"
              element={
                <RequirePermission permission="section:traceability:read">
                  <TraceabilityPage />
                </RequirePermission>
              }
            />

            {/* Magacin: roba na stanju po skladištu, polju i LOT-u, iz ERP-a */}
            <Route
              path="/warehouse"
              element={
                <RequirePermission permission="section:warehouse:read">
                  <WarehousePage />
                </RequirePermission>
              }
            />
            {/* Spisak kvaliteta: proizvodni magacin, LOT-ovi bez statusa */}
            <Route
              path="/quality/unprocessed-lots"
              element={<Navigate to="/warehouse?production=1&status=OPEN" replace />}
            />

            {/* Ugovori o otkupu / procurement contracts */}
            <Route
              path="/procurement/contracts"
              element={
                <RequirePermission permission="section:procurement_contracts:read">
                  <ProcurementContractsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/contracts/:id"
              element={
                <RequirePermission permission="section:procurement_contracts:read">
                  <ProcurementContractDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/procurement/contracts"
              element={
                <RequirePermission permission="section:procurement_contracts:read">
                  <AdminProcurementContractsPage />
                </RequirePermission>
              }
            />
            {/* Predlozi ugovora pročitani iz evidencije otkupa; odluku donosi čovek. */}
            <Route
              path="/procurement/imports"
              element={
                <RequirePermission permission="section:procurement_imports:read">
                  <ProcurementImportsPage />
                </RequirePermission>
              }
            />

            <Route
              path="/admin/quality/parameters"
              element={
                <RequirePermission permission="section:quality_parameters:read">
                  <AdminQualityParametersPage />
                </RequirePermission>
              }
            />

            <Route
              path="/admin/quality/test-plans"
              element={
                <RequirePermission permission="section:test_plans:read">
                  <AdminTestPlansPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/production-steps"
              element={
                <RequirePermission permission="section:production_steps:read">
                  <ProductionStepsPage />
                </RequirePermission>
              }
            />

            <Route
              path="/sales/dispatch-orders"
              element={
                <RequirePermission permission="section:dispatch_orders:read">
                  <DispatchOrdersPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/readings"
              element={
                <RequirePermission permission="section:quality_readings:read">
                  <QualityLotReadingsPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/queue"
              element={
                <RequirePermission permission="section:quality_readings:read">
                  <QualityWorkQueuePage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/awaiting-verification"
              element={
                <RequirePermission permission="section:quality_readings:read">
                  <QualityPendingVerificationPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/dispatch-release"
              element={
                <RequirePermission permission="section:dispatch_quality_release:read">
                  <QualityDispatchReleasePage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/lab-archive"
              element={
                <RequirePermission permission="section:lab_archive:read">
                  <LabArchivePage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/control-points"
              element={
                <RequirePermission permission="section:control_points:read">
                  <QualityControlPointsPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/external-analyses"
              element={
                <RequirePermission permission="section:external_analyses:read">
                  <ExternalAnalysesPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/external-laboratories"
              element={
                <RequirePermission permission="section:external_labs:read">
                  <ExternalLaboratoriesPage />
                </RequirePermission>
              }
            />

            {/* Klase sirovine: sifarnik koji odlucuje u koji silos roba ide i u koje
                proizvode sme. Citanje i uredjivanje su dve dozvole; ekran sam skriva dugmad. */}
            <Route
              path="/admin/quality/classes"
              element={
                <RequirePermission permission="section:quality_classes:read">
                  <QualityClassesPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/retained-samples"
              element={
                <RequirePermission permission="section:retained_samples:read">
                  <RetainedSamplesPage />
                </RequirePermission>
              }
            />

            <Route
              path="/quality/lot-documents"
              element={
                <RequirePermission permission="section:lot_documents:read">
                  <LotDocumentsPage />
                </RequirePermission>
              }
            />

            <Route
              path="/admin/settlement/rules"
              element={
                <RequirePermission permission="section:settlement_rules:read">
                  <AdminSettlementRulesPage />
                </RequirePermission>
              }
            />

            <Route
              path="/procurement/finance/:id/combined"
              element={
                <RequirePermission permission="section:supplier_finance:read">
                  <CombinedLedgerPage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/settlements"
              element={
                <RequirePermission permission="section:supplier_settlements:read">
                  <SupplierSettlementsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/lots"
              element={
                <RequirePermission permission="section:intake_lots:read">
                  <LotsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/lots/:id"
              element={
                <RequirePermission permission="section:intake_lots:read">
                  <IntakeLotDetailPage />
                </RequirePermission>
              }
            />
            {/* Rucni registar sarzi (`receiving_lot`) ima svoj detalj i svoju putanju: to je drugi
                pojam od prijemne sarze iz ERP-a, i mesanje to dvoje je vec jednom dalo prazan ekran. */}
            <Route
              path="/procurement/receiving-lots/:id"
              element={
                <RequirePermission permission="section:intake_lots:read">
                  <LotDetailPage />
                </RequirePermission>
              }
            />

            {/* Isporuke i kvalitet / deliveries */}
            <Route
              path="/procurement/deliveries"
              element={
                <RequirePermission anyOf={["section:delivery_intake:read", "section:delivery_quality:read", "section:delivery_refusal:read", "section:delivery_documents:read"]}>
                  <DeliveriesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/deliveries/:id"
              element={
                <RequirePermission anyOf={["section:delivery_intake:read", "section:delivery_quality:read", "section:delivery_refusal:read", "section:delivery_documents:read"]}>
                  <DeliveryDetailPage />
                </RequirePermission>
              }
            />

            {/* Finansije dobavljača / supplier finance */}
            <Route
              path="/procurement/finance"
              element={
                <RequirePermission permission="section:supplier_finance:read">
                  <SupplierFinancePage />
                </RequirePermission>
              }
            />
            <Route
              path="/procurement/finance/companies/:id"
              element={
                <RequirePermission permission="section:supplier_finance:read">
                  <SupplierFinanceCompanyPage />
                </RequirePermission>
              }
            />

            {/* Transport Manager — trips board, detail,
                gate and the carriers/vehicles/drivers admin screen. */}
            <Route
              path="/transport/trips"
              element={
                <RequirePermission anyOf={["section:transport_inbound:read", "section:transport_outbound:read"]}>
                  <TripsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/transport/trips/:id"
              element={
                <RequirePermission anyOf={["section:transport_inbound:read", "section:transport_outbound:read"]}>
                  <TripDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/transport/gate"
              element={
                <RequirePermission permission="section:transport_gate:read">
                  <GatePage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/transport/carriers"
              element={
                <RequirePermission permission="section:carriers:read">
                  <CarriersPage />
                </RequirePermission>
              }
            />

            <Route path="/admin/permissions" element={<RequirePermission permission="section:access_control:read"><AdminPermissionsPage /></RequirePermission>} />
            <Route path="/orders/new" element={<RequirePermission permission="section:orders:write"><OrderEditorPage /></RequirePermission>} />
            <Route path="/offers/new" element={<RequirePermission permission="section:offers:write"><OfferEditorPage /></RequirePermission>} />
            {/* Orders */}
            <Route
              path="/orders"
              element={
                <RequirePermission permission="section:orders:read">
                  <OrdersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <RequirePermission permission="section:orders:read">
                  <OrderEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/orders/imports/:id"
              element={
                <RequirePermission permission="section:order_imports:read">
                  <OrderImportReviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <RequirePermission permission="section:orders:read">
                  <AdminOrdersPage />
                </RequirePermission>
              }
            />

            {/* Offers (quotes) — kanban board is the default view, table lives at /offers/list */}
            <Route
              path="/offers"
              element={
                <RequirePermission permission="section:offers:read">
                  <OffersBoardPage />
                </RequirePermission>
              }
            />
            <Route
              path="/offers/list"
              element={
                <RequirePermission permission="section:offers:read">
                  <OffersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/offers/funnel"
              element={
                <RequirePermission permission="section:offers:read">
                  <OffersFunnelPage />
                </RequirePermission>
              }
            />
            <Route
              path="/offers/:id"
              element={
                <RequirePermission permission="section:offers:read">
                  <OfferEditorPage />
                </RequirePermission>
              }
            />

            {/* Lidovi (leads) — internal sales lead pipeline; board default, list on /leads/list */}
            <Route
              path="/leads"
              element={
                <RequirePermission permission="section:leads:read">
                  <LeadsBoardPage />
                </RequirePermission>
              }
            />
            <Route
              path="/leads/list"
              element={
                <RequirePermission permission="section:leads:read">
                  <LeadsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/leads/:id"
              element={
                <RequirePermission permission="section:leads:read">
                  <LeadDetailPage />
                </RequirePermission>
              }
            />

            {/* Complaints (reklamacije na kvalitet) */}
            <Route
              path="/complaints"
              element={
                <RequirePermission anyOf={["section:complaint_submission:read", "section:complaint_processing:read", "section:complaint_decisions:read", "section:complaint_team_8d:read", "section:complaint_outcome:read"]}>
                  <ComplaintsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/complaints/new"
              element={
                <RequirePermission permission="section:complaint_submission:write">
                  <ComplaintNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/complaints/:id"
              element={
                <RequirePermission anyOf={["section:complaint_submission:read", "section:complaint_processing:read", "section:complaint_decisions:read", "section:complaint_team_8d:read", "section:complaint_outcome:read"]}>
                  <ComplaintDetailPage />
                </RequirePermission>
              }
            />

            {/* Forecasts */}
            <Route
              path="/forecasts"
              element={
                <RequirePermission permission="section:forecasts:read">
                  <ForecastsPage />
                </RequirePermission>
              }
            />

            {/* Finance */}
            <Route
              path="/finance"
              element={
                <RequirePermission permission="section:customer_finance:read">
                  <FinancePage />
                </RequirePermission>
              }
            />
            <Route
              path="/finance/companies/:id"
              element={
                <RequirePermission permission="section:customer_finance:read">
                  <FinanceCompanyPage />
                </RequirePermission>
              }
            />

            {/* TM (zadaci, projekti, sastanci, tim) */}
            <Route
              path="/tm"
              element={
                <RequirePermission permission="section:tm_activity:read">
                  <TmOverviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks"
              element={
                <RequirePermission permission="section:tm_tasks:read">
                  <TmTasksPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks/new"
              element={
                <RequirePermission permission="section:tm_tasks:write">
                  <TmTaskNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks/:id"
              element={
                <RequirePermission permission="section:tm_tasks:read">
                  <TmTaskDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects"
              element={
                <RequirePermission permission="section:tm_projects:read">
                  <TmProjectsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects/new"
              element={
                <RequirePermission permission="section:tm_projects:write">
                  <RequirePermission permission="tm:manage_team"><TmProjectNewPage /></RequirePermission>
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects/:id"
              element={
                <RequirePermission permission="section:tm_projects:read">
                  <TmProjectDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings"
              element={
                <RequirePermission permission="section:tm_meetings:read">
                  <TmMeetingsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings/new"
              element={
                <RequirePermission permission="section:tm_meetings:write">
                  <TmMeetingNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings/:id"
              element={
                <RequirePermission permission="section:tm_meetings:read">
                  <TmMeetingDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/team"
              element={
                <RequirePermission permission="section:tm_teams:read">
                  <TmTeamsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/activity"
              element={
                <RequirePermission permission="section:tm_activity:read">
                  <TmActivityPage />
                </RequirePermission>
              }
            />

            {/* Settings → "Moj mejl": the caller's own mailbox.
                mail:connect is the same role set that may send from the portal. */}
            <Route
              path="/settings/mail"
              element={
                <RequirePermission permission="section:personal_mail:read">
                  <MailSettingsPage />
                </RequirePermission>
              }
            />

            {/* ERP exchange monitor */}
            <Route
              path="/admin/erp"
              element={
                <RequirePermission permission="section:erp_operations:read">
                  <AdminErpOutboxPage />
                </RequirePermission>
              }
            />
            {/* The day's exchange rate list as the ERP holds it */}
            <Route
              path="/admin/exchange-rates"
              element={
                <RequirePermission permission="section:exchange_rates:read">
                  <AdminExchangeRatesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <RequirePermission permission="section:audit:read">
                  <AdminAuditLogPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/emails/preview"
              element={
                <RequirePermission permission="section:email_templates:read">
                  <AdminEmailPreviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/security/jwt-keys"
              element={
                <RequirePermission permission="section:security_keys:read">
                  <AdminJwtKeysPage />
                </RequirePermission>
              }
            />
            </Route>
          </Route>

          {/* Unmatched paths (and the bare root) fall back to the dashboard. */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Navigate to="/dashboard" replace />
              </RequireAuth>
            }
          />
        </Routes>
        </ConfirmProvider>
        </ThemeProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
